import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { PageProps } from "../types/auth";
import { trackPageVisit } from "../utils/pageTracker";

import styles from './styles/ReportsPage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Report {
  report_id: number;
  report_name: string;
  status: string;
  created_at: string;
}

const ReportsPage: React.FC<PageProps> = ({ user, token, selectedOrgID }) => {
  const [reports, setReports] = useState<Report[]>([]);
  const [error, setError] = useState<string>("");
  const [reportToDelete, setReportToDelete] = useState<Report | null>(null);
  const navigate = useNavigate();
  const hasTrackedRef = useRef(false);

  // Track page visit only once when component mounts
  useEffect(() => {
    if (!hasTrackedRef.current) {
      trackPageVisit("Reports", "/reports");
      hasTrackedRef.current = true;
    }
  }, []);

  const fetchReports = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/reports`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Failed to fetch reports.");
        return;
      }

      const data: Report[] = await response.json();
      setReports(Array.isArray(data) ? data : []);
    } catch (err) {
      setError("An unexpected error occurred.");
    }
  };

  useEffect(() => {
    fetchReports();
  }, [token, selectedOrgID]);

  const handleDelete = async (reportId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/reports/${reportId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Failed to delete report.");
        return;
      }

      // Close the dialog and refresh the reports list
      setReportToDelete(null);
      fetchReports();
    } catch (err) {
      setError("An unexpected error occurred while deleting the report.");
    }
  };

  const handleDownload = async (reportId: number, reportName: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/reports/${reportId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          setError("Report is not ready for download yet.");
        } else {
          const errorData = await response.json();
          setError(errorData.error || "Failed to download report.");
        }
        return;
      }

      // Get the blob from the response
      const blob = await response.blob();
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary anchor element to trigger the download
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportName || 'report'}_${reportId}.pdf`;
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError("An unexpected error occurred while downloading the report.");
    }
  };

  if (error) {
    return <p className={styles.errorMessage}>{error}</p>;
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Reports</h1>

      <div className={styles.runReportSection}>
        <h4 className={styles.runReportTitle}>Run Report</h4>
        <ul className={styles.runReportList}>
          <li className={styles.runReportItem}>
            <a href="/reports/group-details" className={styles.runReportLink}>Group Progress Report</a>
          </li>
          <li className={styles.runReportItem}>
            <a href="/reports/group-attendance" className={styles.runReportLink}>Group Attendance Report</a>
          </li>
        </ul>
      </div>

      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.tableHeader}>Report Run Date</th>
            <th className={styles.tableHeader}>Report Name</th>
            <th className={styles.tableHeader}>Status</th>
            <th className={styles.tableHeader}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {reports.length > 0 ? (
            reports.map((report) => (
              <tr key={report.report_id} className={styles.tableRow}>
                <td className={styles.tableCell}>
                  {new Date(report.created_at).toLocaleDateString()}
                </td>
                <td className={styles.tableCell}>{report.report_name}</td>
                <td className={styles.tableCell}>
                  {report.status === "processing" ? (
                    "Processing..."
                  ) : report.status === "completed" ? (
                    <button
                      onClick={() => handleDownload(report.report_id, report.report_name)}
                      className={styles.tableLink}
                    >
                      Download
                    </button>
                  ) : (
                    report.status
                  )}
                </td>
                <td className={styles.tableCell}>
                  <button
                    onClick={() => setReportToDelete(report)}
                    className={styles.deleteButton}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={4} className={styles.tableCell}>No reports found.</td>
            </tr>
          )}
        </tbody>
      </table>

      {reportToDelete && (
        <div className={styles.dialogOverlay}>
          <div className={styles.dialog}>
            <div className={styles.dialogContent}>
              Delete this report?
            </div>
            <div className={styles.dialogButtons}>
              <button
                className={styles.dialogCancelButton}
                onClick={() => setReportToDelete(null)}
              >
                Cancel
              </button>
              <button
                className={styles.dialogButton}
                onClick={() => handleDelete(reportToDelete.report_id)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsPage; 